import { computed, effect, inject, untracked } from '@angular/core';
import { patchState, signalStoreFeature, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { ItemId } from 'products-data';
import { filter } from 'rxjs';
import { EntityRecord, SignalStoreFeatureType } from 'state-data';
import { CartsStore } from '../../data-stores/carts.store';
import { CartEntity } from '../../types';

export const withCart = () => {
  return signalStoreFeature(
    withState(() => ({
      cartId: undefined as string | undefined,
      _record: undefined as EntityRecord<CartEntity> | undefined,
    })),
    withComputed((store) => {
      const cartStore = inject(CartsStore);
      const cartLoading = computed(() => store._record()?.loading ?? true);
      const cartError = computed(() => store._record()?.error ?? null);
      const cart = computed(() => store._record()?.entity ?? null);
      const cartType = computed(() => cart()?.type ?? null);
      const cartCustomerNumber = computed(() => cart()?.customerNumber ?? null);
      const cartItems = computed(() => cart()?.items ?? []);
      const cartItemCount = computed(() => cartItems().length);
      const cartNumericId = computed(() => store._record()?.entity?.id);

      const pendingCartItems = computed(() => Array.from(cartStore.pendingItems()[store.cartId() ?? ''] ?? []));

      return {
        cart,
        cartLoading,
        cartError,
        cartType,
        cartCustomerNumber,
        cartItems,
        pendingCartItems,
        cartItemCount,
        cartNumericId,
      };
    }),
    withProps((store) => {
      const cartStore = inject(CartsStore);
      return {
        cartEvents$: cartStore.events$.pipe(filter((e) => e.cartId === untracked(store.cartId))),
      };
    }),
    withMethods((store) => {
      const cartStore = inject(CartsStore);

      const isItemPending = (itemId: ItemId) => {
        const id = store.cartId();
        return id ? cartStore.isItemPending(id, itemId) : false;
      };
      const updateItemQuantity = (itemId: ItemId, quantity: number) => {
        const id = store.cartId();
        if (id) {
          cartStore.updateItemQuantity(id, itemId, quantity);
        }
      };

      const clearCart = () => {
        const id = store.cartId();
        if (id) {
          cartStore.clearCart(id);
        }
      };

      const _selectCartById = (cartId: string) => {
        patchState(store, { cartId });
      };

      const _selectCart = (cartType: string, customerNumber: string) => {
        patchState(store, { cartId: `${cartType}|${customerNumber}` });
      };

      return {
        clearCart,
        updateItemQuantity,
        isItemPending,
        _selectCartById,
        _selectCart,
      };
    }),
    withHooks((store) => {
      const cartStore = inject(CartsStore);
      return {
        onInit() {
          effect(() => {
            const id = store.cartId();
            patchState(store, {
              _record: id ? cartStore.get(id)() : undefined,
            });
          });
        },
      };
    }),
  );
};

export type WithCartFeature = SignalStoreFeatureType<ReturnType<typeof withCart>>;
